// The Inbox's one entry (design 2026-10-30 ia-inbox-files-chat §3; founder decisions 2026-10-30
// INBOX-PAGE-MAIN and SHELL-DECISIONS "one Inbox entry"): the full page `/c/{slug}/inbox` is the
// Inbox, and the rail's Inbox item (under Desk) — on phones the bottom bar's — is the only way in.
// A click opens the page. Its badge is what waits on the person ("Waiting on me": approvals, a
// DE's question), the list the page's first section and the peek show (presenting mutes it).
//
// The quick peek (it replaced the 560 px drawer, then the top bar's bell, now retired): the top
// five "Waiting on me" items with their inline primary action (Approve / Edit / Reject, Reply,
// Resume, Open) and "Open Inbox". It opens when a mouse rests on the item (a short delay, and a
// short grace to cross onto it, so it is not twitchy) or on a long press on touch; it closes on
// leaving both, a click away, Escape and any navigation. Everything else (tabs, Filter, Mark all
// read, the crew's suggestions, j/k/e/s/u) is on the page.
import {
  BottomBarItem,
  Button,
  CountPill,
  EmptyState,
  Popover,
  PopoverContent,
  Skeleton,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, useRouterState } from "@tanstack/react-router";
import { ArrowRightIcon, HammerIcon, InboxIcon } from "lucide-react";
import {
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import type { Api } from "../api/client";
import type { CompanyAccess } from "../features/company";
import { InboxList } from "../features/work/inbox-list";
import { inboxQuery, workLiveQuery } from "../features/work/work-queries";
import { useShellState } from "./shell-state";

/** The peek's rows: the top of Waiting on me. */
export const PEEK_ROWS = 5;
/** A mouse resting this long on the Inbox item opens the peek. */
export const PEEK_OPEN_DELAY_MS = 300;
/** Leaving the item and the peek closes it after this grace (time to cross the gap). */
export const PEEK_CLOSE_DELAY_MS = 250;
/** A touch held this long opens the peek (and the tap's navigation is dropped). */
export const PEEK_LONG_PRESS_MS = 500;
/** A touch that moves farther than this before the long press fires is a scroll, not a press. */
export const PEEK_MOVE_TOLERANCE_PX = 10;

/** Whether `pathname` is a company's Inbox page. */
export const isInboxPage = (pathname: string) => /^\/c\/[^/]+\/inbox\/?$/.test(pathname);

function useOnInboxPage(): boolean {
  return useRouterState({ select: (s) => isInboxPage(s.location.pathname) });
}

/**
 * "Waiting on me": the badge's count (none while presenting or at zero), whether more than one
 * page (100) waits (the counts then read "at least"), and whether this caller has an inbox here
 * (DEs and agency users without a membership do not: no badge, no peek).
 */
function useWaiting(api: Api, companyId: string) {
  const shell = useShellState();
  const waiting = useQuery(inboxQuery(api, companyId, "waiting"));
  const n = waiting.data?.data.length ?? 0;
  const more = !!waiting.data?.next_cursor;
  const count = shell.presenting || !n ? undefined : n;
  return { n, more, count, served: waiting.data !== null };
}

const waitingLabel = (count: number | undefined, more: boolean) =>
  count ? `Inbox, ${count}${more ? "+" : ""} waiting on me` : "Inbox";

/**
 * The peek's open state and the item's and peek's pointer handlers: a mouse hover opens it after
 * `PEEK_OPEN_DELAY_MS` and leaving closes it after `PEEK_CLOSE_DELAY_MS` (re-entering either
 * keeps it, and acting inside pins it); a long touch opens it and swallows the click that
 * follows. Any navigation closes it.
 */
function usePeekOpen(enabled: boolean) {
  const [open, setOpen] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const pressed = useRef(false);
  const start = useRef<{ x: number; y: number } | null>(null);
  const clear = useCallback(() => {
    if (timer.current !== undefined) window.clearTimeout(timer.current);
    timer.current = undefined;
  }, []);
  const later = (ms: number, run: () => void) => {
    clear();
    timer.current = window.setTimeout(() => {
      timer.current = undefined;
      run();
    }, ms);
  };
  useEffect(() => clear, [clear]);

  const href = useRouterState({ select: (s) => s.location.href });
  const last = useRef(href);
  useEffect(() => {
    if (last.current === href) return;
    last.current = href;
    clear();
    setOpen(false);
  }, [href, clear]);

  // Once the person acts inside the peek (a click, the keyboard), leaving no longer closes it: a
  // row's Reject dialog opens above it, and only a click away, Escape or navigation close it.
  const pinned = useRef(false);
  useEffect(() => {
    if (!open) pinned.current = false;
  }, [open]);
  const mouse = (e: ReactPointerEvent) => e.pointerType === "mouse";
  const leave = (e: ReactPointerEvent) => {
    if (mouse(e) && !pinned.current) later(PEEK_CLOSE_DELAY_MS, () => setOpen(false));
  };
  const trigger = enabled
    ? {
        onPointerEnter: (e: ReactPointerEvent) => {
          if (!mouse(e)) return;
          if (open) clear();
          else later(PEEK_OPEN_DELAY_MS, () => setOpen(true));
        },
        onPointerLeave: leave,
        onPointerDown: (e: ReactPointerEvent) => {
          pressed.current = false;
          if (mouse(e)) {
            // A click goes to the page: no peek opening under it.
            clear();
            return;
          }
          start.current = { x: e.clientX, y: e.clientY };
          later(PEEK_LONG_PRESS_MS, () => {
            pressed.current = true;
            setOpen(true);
          });
        },
        // A press that moves (a scroll or a drag) is no long press.
        onPointerMove: (e: ReactPointerEvent) => {
          const s = start.current;
          if (mouse(e) || !s || pressed.current) return;
          if (Math.hypot(e.clientX - s.x, e.clientY - s.y) > PEEK_MOVE_TOLERANCE_PX) {
            start.current = null;
            clear();
          }
        },
        onPointerUp: (e: ReactPointerEvent) => {
          if (!mouse(e) && !pressed.current) clear();
        },
        onPointerCancel: (e: ReactPointerEvent) => {
          if (!mouse(e)) clear();
        },
        // The long press opened the peek: no system menu, and the tap's click does not navigate.
        onContextMenu: (e: ReactMouseEvent) => {
          if (pressed.current || timer.current !== undefined) e.preventDefault();
        },
        onClick: (e: ReactMouseEvent) => {
          if (!pressed.current) return;
          pressed.current = false;
          e.preventDefault();
        },
      }
    : {};
  const popup = {
    onPointerEnter: (e: ReactPointerEvent) => {
      if (mouse(e)) clear();
    },
    onPointerLeave: leave,
    onPointerDown: () => {
      clear();
      pinned.current = true;
    },
    onFocus: () => {
      clear();
      pinned.current = true;
    },
  };
  const change = (next: boolean) => {
    clear();
    setOpen(next);
  };
  return { open, setOpen: change, trigger, popup };
}

/** The rail item's look (the ui kit's RailItem, without its tooltip: the peek is the hover). */
const RAIL_ITEM =
  "relative flex size-12 flex-col items-center justify-center gap-0.5 rounded-md text-secondary transition-colors duration-(--dur-fast) ease-out hover:bg-muted hover:text-foreground aria-[current=page]:bg-primary-soft aria-[current=page]:text-primary-text aria-expanded:bg-muted aria-expanded:text-foreground outline-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-ring";

/** The rail's Inbox item (global, every mode): opens the Inbox page; hover peeks. */
export function InboxRailItem({ api, company }: { api: Api; company: CompanyAccess }) {
  const here = useOnInboxPage();
  const { n, more, count, served } = useWaiting(api, company.id);
  const peek = usePeekOpen(served);
  const ref = useRef<HTMLAnchorElement>(null);
  return (
    <li>
      <Link
        ref={ref}
        to="/c/$slug/inbox"
        params={{ slug: company.slug }}
        data-slot="rail-item"
        data-mode="inbox"
        aria-label={waitingLabel(count, more)}
        aria-current={here ? "page" : undefined}
        aria-expanded={served ? peek.open : undefined}
        aria-haspopup={served ? "dialog" : undefined}
        className={RAIL_ITEM}
        {...peek.trigger}
      >
        <InboxIcon aria-hidden="true" className="size-5" />
        <span className="max-w-full truncate text-[10px] leading-3 font-medium tracking-[-0.2px]">
          Inbox
        </span>
        {count ? (
          <CountPill count={count} tone="accent" className="absolute top-0.5 right-0.5" />
        ) : null}
      </Link>
      {served ? (
        <PeekPanel
          api={api}
          company={company}
          anchor={ref}
          side="right"
          n={n}
          more={more}
          count={count}
          peek={peek}
        />
      ) : null}
    </li>
  );
}

/** Phones (NAV-REDESIGN): the Inbox in the bottom bar, the same page and count; a long press peeks. */
export function InboxBottomItem({ api, company }: { api: Api; company: CompanyAccess }) {
  const here = useOnInboxPage();
  const { n, more, count, served } = useWaiting(api, company.id);
  const peek = usePeekOpen(served);
  const ref = useRef<HTMLAnchorElement>(null);
  return (
    <>
      <BottomBarItem
        icon={InboxIcon}
        label="Inbox"
        count={count}
        active={here}
        data-mode="inbox"
        aria-label={waitingLabel(count, more)}
        {...peek.trigger}
        render={
          <Link ref={ref} to="/c/$slug/inbox" params={{ slug: company.slug }} className="w-full" />
        }
      />
      {served ? (
        <PeekPanel
          api={api}
          company={company}
          anchor={ref}
          side="top"
          n={n}
          more={more}
          count={count}
          peek={peek}
        />
      ) : null}
    </>
  );
}

/** The quick peek: the top of Waiting on me with inline actions, and "Open Inbox". */
function PeekPanel({
  api,
  company,
  anchor,
  side,
  n,
  more,
  count,
  peek,
}: {
  api: Api;
  company: CompanyAccess;
  anchor: RefObject<HTMLElement | null>;
  side: "right" | "top";
  n: number;
  more: boolean;
  count: number | undefined;
  peek: ReturnType<typeof usePeekOpen>;
}) {
  const { open, setOpen } = peek;
  const live = useQuery({ ...workLiveQuery(api, company.id), enabled: open });
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverContent
        anchor={anchor}
        side={side}
        align={side === "right" ? "start" : "center"}
        sideOffset={side === "right" ? 8 : 6}
        aria-label="Waiting on me"
        data-slot="inbox-peek"
        // A hover never takes the keyboard away from where it was.
        initialFocus={false}
        finalFocus={false}
        {...peek.popup}
        // Below dialogs: a row's Edit / Reject dialog opens above the peek.
        positionerClassName="z-drawer"
        className="max-h-[min(36rem,calc(100dvh-5rem))] w-[min(28rem,calc(100vw-2rem))] gap-0 overflow-hidden p-0"
      >
        <div className="flex h-11 shrink-0 items-center gap-2 border-b border-subtle px-4">
          <h2 className="text-sm font-semibold">Waiting on me</h2>
          {count ? <CountPill count={count} tone="accent" /> : null}
        </div>
        {!open ? null : live.data === true ? (
          <div className="flex min-h-0 flex-1 flex-col">
            <InboxList
              api={api}
              company={company}
              peek
              limit={PEEK_ROWS}
              onNavigated={() => setOpen(false)}
            />
          </div>
        ) : live.data === false ? (
          <EmptyState
            variant="compact"
            icon={HammerIcon}
            title="The inbox is not live on this server yet"
            description="Its routes arrive with the inbox build (M2-04). Nothing here is sample data."
          />
        ) : (
          <div role="status" aria-label="Loading the inbox" className="flex flex-col gap-2 p-4">
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
          </div>
        )}
        <div className="flex h-10 shrink-0 items-center border-t border-subtle px-2">
          {n > PEEK_ROWS ? (
            <span className="px-2 text-xs text-muted-foreground tabular-nums">
              {n - PEEK_ROWS}
              {more ? "+" : ""} more waiting
            </span>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto"
            data-slot="inbox-peek-open"
            render={<Link to="/c/$slug/inbox" params={{ slug: company.slug }} />}
            onClick={() => setOpen(false)}
          >
            Open Inbox
            <ArrowRightIcon data-icon="inline-end" />
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
